import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Lock, Instagram, Facebook, ArrowUpRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const normalizePhoneHref = (phone) => {
  let digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('0')) digits = '+33' + digits.slice(1);
  return `tel:${digits}`;
};

const easeLuxe = [0.16, 1, 0.3, 1];

const NAV_LINKS = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'Hebergements', href: '#hebergements' },
  { label: 'Evenements', href: '#evenements' },
  { label: 'Services', href: '#services' },
  { label: 'Galerie', href: '#galerie' },
  { label: 'Avis', href: '#avis' },
];

const STAY_LINKS = [
  { label: "L'Appartement Terrasse", href: '#hebergements' },
  { label: "L'Appartement Panorama", href: '#hebergements' },
  { label: 'Gite seul', href: '#hebergements' },
  { label: 'Gite + Salle des fetes', href: '#hebergements' },
  { label: 'Disponibilites', href: '#calendrier' },
  { label: 'Reserver', href: '#reservation' },
];

const PRACTICAL_LINKS = [
  { label: 'Borne de recharge EV', href: '#services' },
  { label: 'Oeufs frais du domaine', href: '#services' },
  { label: 'Cuisine professionnelle', href: '#services' },
  { label: 'Acces & transferts', href: '#services' },
  { label: 'Foire aux questions', href: '/cgv' },
  { label: 'Conditions generales', href: '/cgv' },
];

const SOCIALS = [
  { label: 'Instagram', href: 'https://instagram.com', Icon: Instagram },
  { label: 'Facebook', href: 'https://facebook.com', Icon: Facebook },
];

const ADDRESS_QUERY = 'Le Domaine du Pilat, Parc Naturel Regional du Pilat, Saint-Etienne';
// OpenStreetMap - no API key needed. Lat/Long approximate Pilat / Saint-Etienne area
const MAP_EMBED = 'https://www.openstreetmap.org/export/embed.html?bbox=4.42%2C45.30%2C4.85%2C45.55&layer=mapnik&marker=45.4397%2C4.6172';
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS_QUERY)}`;

export default function Footer() {
  const [contactPhone, setContactPhone] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/contact-info`)
      .then((r) => setContactPhone((r.data?.phone || '').trim()))
      .catch(() => setContactPhone(''));
  }, []);

  const scrollOrNavigate = (href) => {
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      window.location.hash = href;
    } else {
      window.location.href = href;
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="bg-[#0E0F0D] text-white relative overflow-hidden"
    >
      {/* Soft gold radial glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] rounded-full"
        style={{ background: 'radial-gradient(ellipse at center, rgba(203,167,122,0.06) 0%, transparent 70%)' }}
      />

      {/* Top brand + map */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: easeLuxe }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-20"
        >
          <div className="lg:col-span-7">
            <h3 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.025em] leading-[1.02]">
              Le Domaine
              <span className="block italic text-white/55">du Pilat</span>
            </h3>
            <p className="text-white/50 text-sm md:text-base leading-relaxed font-light max-w-md mt-6">
              Gite de charme et salle de reception au coeur du Parc Naturel Regional du Pilat,
              a 30 minutes de Saint-Etienne &middot; 1h de Lyon.
            </p>
            <a
              href="mailto:contact@ledomainedupilat.fr"
              data-testid="footer-mail-cta"
              onClick={() => trackEvent('contact_email_clic', { emplacement: 'footer_cta' })}
              className="group inline-flex items-center gap-2 mt-10 text-white hover:text-[#CBA77A] text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 pb-1 border-b border-white/30 hover:border-[#CBA77A]"
            >
              <span>Nous contacter</span>
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </a>
          </div>

          <div className="lg:col-span-5">
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-map-link"
              className="block relative rounded-2xl overflow-hidden border border-white/[0.06] aspect-[16/10] group"
            >
              <iframe
                title="Carte du Domaine du Pilat"
                src={MAP_EMBED}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, filter: 'invert(0.92) hue-rotate(180deg) saturate(0.5) brightness(0.85)' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Subtle dark overlay for cohesion */}
              <div className="absolute inset-0 bg-black/15 pointer-events-none group-hover:bg-black/5 transition-colors duration-500" />
              <div className="absolute bottom-4 left-4 right-4 backdrop-blur-md bg-black/55 border border-white/10 rounded-xl p-3.5 flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#CBA77A] flex-shrink-0 mt-0.5" strokeWidth={1.7} />
                <div className="text-[12px] leading-snug flex-1">
                  <p className="text-white font-medium tracking-wide">Parc du Pilat &middot; Loire</p>
                  <p className="text-white/55 mt-0.5 text-[11px]">Saint-Etienne, France</p>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-white/45 group-hover:text-[#CBA77A] transition-colors flex-shrink-0 mt-0.5" strokeWidth={1.8} />
              </div>
            </a>
          </div>
        </motion.div>

        {/* 4-column links + contact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          <div>
            <h4 className="overline mb-6">Navigation</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    type="button"
                    onClick={() => scrollOrNavigate(l.href)}
                    className="text-white/55 hover:text-[#CBA77A] text-[11px] font-medium tracking-[0.18em] uppercase transition-colors duration-300"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-6">Sejourner</h4>
            <ul className="space-y-3">
              {STAY_LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    type="button"
                    onClick={() => scrollOrNavigate(l.href)}
                    className="text-white/55 hover:text-[#CBA77A] text-[11px] font-medium tracking-[0.18em] uppercase transition-colors duration-300 text-left"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-6">Infos pratiques</h4>
            <ul className="space-y-3">
              {PRACTICAL_LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    type="button"
                    onClick={() => scrollOrNavigate(l.href)}
                    className="text-white/55 hover:text-[#CBA77A] text-[11px] font-medium tracking-[0.18em] uppercase transition-colors duration-300 text-left"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-6">Contact</h4>
            <ul className="space-y-4 mb-7">
              <li>
                <a
                  href="mailto:contact@ledomainedupilat.fr"
                  data-testid="footer-email"
                  onClick={() => trackEvent('contact_email_clic', { emplacement: 'footer_contact' })}
                  className="group inline-flex items-start gap-2.5 text-white/55 hover:text-[#CBA77A] transition-colors duration-300"
                >
                  <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                  <span className="text-[11px] tracking-wide font-light">contact@ledomainedupilat.fr</span>
                </a>
              </li>
              <li>
                {contactPhone ? (
                  <a
                    href={normalizePhoneHref(contactPhone)}
                    data-testid="footer-phone"
                    onClick={() => trackEvent('contact_telephone_clic', { emplacement: 'footer_contact' })}
                    className="group inline-flex items-center gap-2.5 text-white/55 hover:text-[#CBA77A] transition-colors duration-300"
                  >
                    <Phone className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.6} />
                    <span className="text-[11px] tracking-wide font-light">{contactPhone}</span>
                  </a>
                ) : (
                  <a
                    href="mailto:contact@ledomainedupilat.fr"
                    data-testid="footer-phone-fallback"
                    onClick={() => trackEvent('contact_email_clic', { emplacement: 'footer_telephone_sur_demande' })}
                    className="group inline-flex items-center gap-2.5 text-white/55 hover:text-[#CBA77A] transition-colors duration-300"
                  >
                    <Phone className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.6} />
                    <span className="text-[11px] tracking-wide font-light">Sur demande</span>
                  </a>
                )}
              </li>
              <li className="inline-flex items-start gap-2.5 text-white/55">
                <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                <span className="text-[11px] tracking-wide font-light leading-relaxed">
                  Check-in 16h<br />Check-out 12h<br />
                  <span className="text-white/35">Boite a cles 24/24</span>
                </span>
              </li>
            </ul>

            {/* Socials */}
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="group w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#CBA77A]/60 hover:bg-[#CBA77A]/10 transition-all duration-300"
                >
                  <Icon className="w-3.5 h-3.5 text-white/55 group-hover:text-[#CBA77A] transition-colors duration-300" strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gold separator */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="h-px bg-gradient-to-r from-transparent via-[#CBA77A]/30 to-transparent" />
      </div>

      {/* Bottom strip */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10px] text-white/30 uppercase tracking-[0.18em]">
            <a href="/cgv" data-testid="footer-cgv-link" className="hover:text-white/60 transition-colors">CGV</a>
            <a href="/mentions-legales" data-testid="footer-mentions-link" className="hover:text-white/60 transition-colors">Mentions legales</a>
            <a href="/confidentialite" data-testid="footer-confidentialite-link" className="hover:text-white/60 transition-colors">Confidentialite</a>
            <a href="/confidentialite" className="hover:text-white/60 transition-colors">Cookies</a>
            <a href="/admin" data-testid="admin-footer-link" className="hover:text-white/60 transition-colors inline-flex items-center gap-1.5">
              <Lock className="w-2.5 h-2.5" strokeWidth={1.8} />
              Espace gestion
            </a>
          </div>
          <p className="text-[10px] text-white/30 uppercase tracking-[0.18em]">
            &copy; {new Date().getFullYear()} Le Domaine du Pilat
          </p>
        </div>
      </div>
    </footer>
  );
}
